import { CREDIT_USER_BLOCKED, MAX_ENROLLMENTS_ERROR_CODE } from 'constants/errors'
import { EM_DASH } from 'constants/component'
import * as get from 'lodash/get'
import { Member } from 'interfaces/member'
import { CreditExtendedFormValues, CreditFormValues, CreditLinkagePhase } from 'interfaces/credit'
import { AddressFieldsValues } from 'interfaces/property'
import { formatUSDate } from 'helpers/dateTimeHelpers'
import { cityToOption } from 'helpers/cityHelpers'

const creditGrades = [
  {
    minimalScore: 781,
    grade: 'A',
    label: 'Excellent',
  },
  {
    minimalScore: 721,
    grade: 'B',
    label: 'Great',
  },
  {
    minimalScore: 661,
    grade: 'C',
    label: 'Good',
  },
  {
    minimalScore: 601,
    grade: 'D',
    label: 'Fair',
  },
  {
    minimalScore: 500,
    grade: 'F1',
    label: 'Poor',
  },
  {
    minimalScore: 300,
    grade: 'F2',
    label: 'Poor',
  },
]

export function getCreditStatusGrade (score: number) {
  const found = creditGrades.find((grade) => score >= grade.minimalScore)
  const result = found || creditGrades.slice(-1)[0]

  return Object.assign({}, result)
}

export function getCreditStatusGradeLabel (score: number) {
  return getCreditStatusGrade(score).label
}

export const formatFullCreditscore = (creditscore: number) =>
  `${ getCreditStatusGradeLabel(creditscore) } ${ EM_DASH } ${ creditscore }`

const BLOCKING_ERRORS_SET = new Set([MAX_ENROLLMENTS_ERROR_CODE, CREDIT_USER_BLOCKED])
export const isBlockingError = (errorCode: string) =>
  BLOCKING_ERRORS_SET.has(errorCode)

const pickCreditFormCommonValues = ({ address1, address2, city, zip5 }: Partial<Member>): AddressFieldsValues => ({
  address1: address1 || '',
  address2: address2 || '',
  city: city ? cityToOption(city) : null,
  zip5: zip5 || '',
})

export const getCreditFormInitialValues = (
  member: Partial<Member>,
  phase: CreditLinkagePhase,
  creditFormValues: CreditFormValues | CreditExtendedFormValues | null,
) => {
  const commonValues: AddressFieldsValues = pickCreditFormCommonValues(member)

  const memberInitialValues: CreditFormValues | CreditExtendedFormValues =
    (phase === 'form-extended')
      ? {
        ...commonValues,
        ssn: '',
        first_name: member.first_name || '',
        last_name: member.last_name || '',
        birth_date: member.birth_date ? formatUSDate(member.birth_date) : '',
      }
      : {
        ...commonValues,
        ssn4: member.ssn4 || '',
      }

  return Object.keys(memberInitialValues).reduce<CreditFormValues | CreditExtendedFormValues>(
    (result, key) => {
      const creditFormValue = get(creditFormValues, key)
      const memberInitialValue = get(memberInitialValues, key)
      const isMemberInitialValueBetter = (key === 'zip5' || key === 'city') && Boolean(memberInitialValue)

      return {
        ...result,
        [key]: isMemberInitialValueBetter
          ? memberInitialValue
          : creditFormValue || memberInitialValue,
      }
    },
    memberInitialValues
  )
}
